import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Dialog, IconButton } from '@mui/material';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import ImageCustom from 'commons/ImageCustom';

import useStyles from './styles';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const ArtworkImageLightBox = ({ open, onClose, images, initialIndex }) => {
  const classes = useStyles();

  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const thumbnailRefs = useRef([]);
  const imageContainerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex || 0);
      resetZoom();
    }
  }, [initialIndex, open]);

  useEffect(() => {
    resetZoom();
  }, [currentIndex]);

  useEffect(() => {
    if (thumbnailRefs.current[currentIndex]) {
      thumbnailRefs.current[currentIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [currentIndex]);

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      if (newZoom <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    },
    [handleZoomIn, handleZoomOut],
  );

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex < images.length - 1 ? prevIndex + 1 : 0));
  }, [images.length]);

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const handleKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          resetZoom();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    },
    [handleZoomIn, handleZoomOut, handleNext, handlePrevious, onClose],
  );

  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, handleKeyDown]);

  if (!images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  const zoomPercentage = Math.round(zoom * 100);
  const showNavigation = images.length > 1;
  const showThumbnails = images.length > 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      maxWidth={false}
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <Box className={classes.container}>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>

        <Box className={classes.zoomControls}>
          <IconButton
            className={classes.zoomButton}
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            aria-label="zoom out"
          >
            <RemoveIcon />
          </IconButton>

          <Box className={classes.zoomIndicator}>{zoomPercentage}%</Box>

          <IconButton
            className={classes.zoomButton}
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            aria-label="zoom in"
          >
            <AddIcon />
          </IconButton>

          <IconButton
            className={classes.zoomButton}
            onClick={resetZoom}
            aria-label="reset zoom"
          >
            <RestartAltIcon />
          </IconButton>
        </Box>

        {showThumbnails && (
          <Box className={classes.sidebar}>
            {images.map((image, index) => (
              <Box
                key={index}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`${classes.thumbnailItem} ${
                  index === currentIndex ? classes.thumbnailItemActive : ''
                }`}
                onClick={() => handleThumbnailClick(index)}
              >
                <ImageCustom
                  src={image}
                  imageStyle={classes.thumbnailImage}
                />
              </Box>
            ))}
          </Box>
        )}

        <Box
          className={classes.centerArea}
          ref={imageContainerRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <Box
            className={classes.imageWrapper}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            }}
          >
            <img
              src={currentImage}
              alt={`Artwork image ${currentIndex + 1}`}
              className={classes.mainImage}
              draggable={false}
            />
          </Box>

          {showNavigation && (
            <>
              <IconButton
                className={`${classes.navigationButton} ${classes.prevButton}`}
                onClick={handlePrevious}
                aria-label="previous"
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              <IconButton
                className={`${classes.navigationButton} ${classes.nextButton}`}
                onClick={handleNext}
                aria-label="next"
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}

          {showNavigation && (
            <Box className={classes.imageCounter}>
              {currentIndex + 1} / {images.length}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ArtworkImageLightBox;
